import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../Logo/Logo';
import styles from './Navigation.module.css';

const LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/#services', label: 'Services' },
  { to: '/#approach', label: 'Approach' },
  { to: '/contact', label: 'Contact' },
];

/** Section links on Home; Home itself is current when none of them is. */
const SECTION_HASHES = LINKS.filter(({ to }) => to.startsWith('/#')).map(({ to }) => to.slice(1));

const isCurrent = (to: string, end: boolean | undefined, pathname: string, hash: string) => {
  if (to.startsWith('/#')) return pathname === '/' && hash === to.slice(1);
  if (end) return pathname === '/' && !SECTION_HASHES.includes(hash);
  return pathname === to;
};

const Navigation = () => {
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Close the drawer on every route change, including a hash jump to a section.
  useEffect(() => setOpen(false), [pathname, hash]);

  // The bar is transparent over the hero and picks up its glass once the page
  // leaves the top.
  useEffect(() => {
    const sync = () => setScrolled(window.scrollY > 8);
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    return () => window.removeEventListener('scroll', sync);
  }, []);

  const linkClass = (to: string, end?: boolean) =>
    isCurrent(to, end, pathname, hash) ? `${styles.link} ${styles.active}` : styles.link;

  // Home opens on the dark photo hero, so the resting bar goes light there.
  const solid = scrolled || open;
  const overDark = pathname === '/' && !solid;

  return (
    <>
      <nav
        className={`${styles.nav} ${solid ? styles.scrolled : ''} ${overDark ? styles.onDark : ''}`}
      >
        <div className={styles.inner}>
          <Link to="/" className={styles.brand} aria-label="Summerlin — home">
            <Logo onDark={overDark} />
          </Link>

          <ul className={styles.links}>
            {LINKS.map(({ to, label, end }) => (
              <li key={to}>
                <Link to={to} className={linkClass(to, end)}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <Link to="/contact" className={styles.cta}>
            Speak with us
          </Link>

          <button
            type="button"
            className={`${styles.hamburger} ${open ? styles.hamburgerOpen : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <div id="mobile-menu" className={`${styles.mobileMenu} ${open ? styles.mobileMenuOpen : ''}`}>
        <ul>
          {LINKS.map(({ to, label, end }) => (
            <li key={to}>
              <Link to={to} className={linkClass(to, end)}>
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default Navigation;
